import qs from 'qs';
import { axiosClient } from '@/libs/axios-client';
import { env } from '@/config/env';
import {
  customUrlEncode,
  prepareFilters,
  preparePagination,
  prepareSorters,
} from '@/utils/apiParamsUtils';

/* ------------------------------ Knowledge base ---------------------------- */

export const listKnowledgeDocuments = async (params) => {
  const filters = prepareFilters(params, { title: 'default' });
  const sorters = prepareSorters(params, {
    createdAt: 'sortCreatedAt',
    updatedAt: 'sortUpdatedAt',
  });
  const pagination = preparePagination(params);

  const response = await axiosClient.get('/assistant/knowledge', {
    params: { filter: filters, sorter: sorters, ...pagination },
    paramsSerializer: (p) => {
      if (p.filter) p.filter = JSON.stringify(p.filter);
      if (p.sorter) p.sorter = JSON.stringify(p.sorter);
      return qs.stringify(p, {
        encode: true,
        arrayFormat: 'brackets',
        encoder: customUrlEncode,
      });
    },
  });
  return response.data;
};

export const createKnowledgeDocument = async (payload) => {
  const response = await axiosClient.post('/assistant/knowledge', payload);
  return response.data;
};

export const uploadKnowledgeDocument = async (formData) => {
  const response = await axiosClient.post(
    '/assistant/knowledge/upload',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
};

export const ingestGuidelines = async () => {
  const response = await axiosClient.post(
    '/assistant/knowledge/ingest-guidelines'
  );
  return response.data;
};

export const updateKnowledgeDocument = async (id, payload) => {
  const response = await axiosClient.put(`/assistant/knowledge/${id}`, payload);
  return response.data;
};

export const reindexKnowledgeDocument = async (id) => {
  const response = await axiosClient.post(
    `/assistant/knowledge/${id}/reindex`
  );
  return response.data;
};

export const deleteKnowledgeDocument = async (id) => {
  const response = await axiosClient.delete(`/assistant/knowledge/${id}`);
  return response.data;
};

/**
 * Pull a binary response down as a file, naming it from `Content-Disposition`
 * when the server sent one.
 */
const saveBlobResponse = (response, fallbackName) => {
  const disposition = response.headers['content-disposition'] || '';
  const utf8 = /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i.exec(disposition);
  const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(disposition);

  let filename = fallbackName;
  if (utf8?.[1]) {
    try {
      filename = decodeURIComponent(utf8[1].replace(/(^")|("$)/g, ''));
    } catch {
      filename = utf8[1].replace(/(^")|("$)/g, '');
    }
  } else if (plain?.[1]) {
    [, filename] = plain;
  }

  const blob = new Blob([response.data], {
    type: response.headers['content-type'] || 'application/octet-stream',
  });
  const url = window.URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.style.display = 'none';
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();

  setTimeout(() => {
    window.URL.revokeObjectURL(url);
    document.body.removeChild(link);
  }, 1000);
};

export const downloadKnowledgeDocument = async (id, fallbackName = 'document') => {
  const response = await axiosClient.get(`/assistant/knowledge/${id}/download`, {
    responseType: 'blob',
  });

  saveBlobResponse(response, fallbackName);
};

export const downloadSourceDocument = async (id, fallbackName = 'document') => {
  const response = await axiosClient.get(
    `/assistant/chat/document/${id}/download`,
    { responseType: 'blob' }
  );

  saveBlobResponse(response, fallbackName);
};

/* ---------------------------------- Chat ---------------------------------- */

export const listConversations = async () => {
  const response = await axiosClient.get('/assistant/chat/conversations');
  return response.data;
};

export const getConversationMessages = async (id) => {
  const response = await axiosClient.get(
    `/assistant/chat/conversations/${id}`
  );
  return response.data;
};

export const escalateToSupport = async (payload) => {
  const response = await axiosClient.post('/assistant/chat/escalate', payload);
  return response.data;
};

export const getSourceDocument = async (id, chunkId) => {
  const response = await axiosClient.get(`/assistant/chat/document/${id}`, {
    params: chunkId ? { chunkId } : undefined,
  });
  return response.data;
};

/**
 * Send a chat message and stream the answer via Server-Sent Events.
 * Uses fetch (axios cannot stream); cookie auth via credentials:'include'.
 *
 * @param {Object} params
 * @param {string} [params.conversationId]
 * @param {string} params.message
 * @param {string} params.language
 * @param {AbortSignal} [params.signal]
 * @param {(text: string) => void} [params.onDelta]
 * @param {(payload: { conversationId, citations, outOfScope }) => void} [params.onDone]
 * @param {(payload: { message }) => void} [params.onError]
 */
export const streamMessage = async ({
  conversationId,
  message,
  language,
  signal,
  onDelta,
  onDone,
  onError,
}) => {
  let response;
  try {
    response = await fetch(`${env.apiURL}/assistant/chat/message`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ conversationId, message, language }),
      signal,
    });
  } catch (error) {
    if (error.name !== 'AbortError') onError?.({ message: error.message });
    return;
  }

  if (!response.ok || !response.body) {
    let message = `HTTP ${response?.status}`;
    try {
      const data = await response.json();
      message = data?.message || message;
    } catch {
      /* ignore */
    }
    onError?.({ message });
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const processFrame = (frame) => {
    const lines = frame.split('\n');
    let event = 'message';
    let data = '';
    lines.forEach((line) => {
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) data += line.slice(5).trim();
    });
    if (!data) return;
    let payload;
    try {
      payload = JSON.parse(data);
    } catch {
      return;
    }
    if (event === 'delta') onDelta?.(payload.text || '');
    else if (event === 'done') onDone?.(payload);
    else if (event === 'error') onError?.(payload);
  };

  try {
    let result = await reader.read();
    while (!result.done) {
      buffer += decoder.decode(result.value, { stream: true });
      const frames = buffer.split('\n\n');
      buffer = frames.pop();
      frames.forEach(processFrame);
      result = await reader.read();
    }
  } catch (error) {
    if (error.name !== 'AbortError') onError?.({ message: error.message });
  }
};

/* --------------------------------- Config --------------------------------- */

export const getPublicConfig = async () => {
  const response = await axiosClient.get('/assistant/config/public');
  return response.data;
};

export const getConfig = async () => {
  const response = await axiosClient.get('/assistant/config');
  return response.data;
};

export const updateConfig = async (payload) => {
  const response = await axiosClient.put('/assistant/config', payload);
  return response.data;
};

/* -------------------------------- Analytics ------------------------------- */

export const getAnalyticsOverview = async (days = 30) => {
  const response = await axiosClient.get('/assistant/analytics/overview', {
    params: { days },
  });
  return response.data;
};

export const getAnalyticsQuestions = async ({
  page = 1,
  pageSize = 20,
  companyId,
  outOfScope,
  question,
  userSearch,
  sorter,
} = {}) => {
  const filter = {};
  if (companyId) filter.companyId = companyId;
  if (typeof outOfScope === 'boolean') filter.outOfScope = outOfScope;
  if (question) filter.question = question;
  if (userSearch) filter.userSearch = userSearch;

  const response = await axiosClient.get('/assistant/analytics/questions', {
    params: {
      filter,
      sorter: sorter || { timestamp: -1 },
      page,
      limit: pageSize,
    },
    paramsSerializer: (p) => {
      if (p.filter) p.filter = JSON.stringify(p.filter);
      if (p.sorter) p.sorter = JSON.stringify(p.sorter);
      return qs.stringify(p, {
        encode: true,
        arrayFormat: 'brackets',
        encoder: customUrlEncode,
      });
    },
  });
  return response.data;
};

export const getAnalyticsThemes = async (companyId) => {
  const response = await axiosClient.get('/assistant/analytics/themes', {
    params: companyId ? { companyId } : undefined,
  });
  return response.data;
};

export const summarizeAnalyticsThemes = async (payload = {}) => {
  const response = await axiosClient.post(
    '/assistant/analytics/themes/summary',
    payload
  );
  return response.data;
};

const AssistantService = {
  listKnowledgeDocuments,
  createKnowledgeDocument,
  uploadKnowledgeDocument,
  ingestGuidelines,
  updateKnowledgeDocument,
  reindexKnowledgeDocument,
  deleteKnowledgeDocument,
  listConversations,
  getConversationMessages,
  escalateToSupport,
  getSourceDocument,
  streamMessage,
  getPublicConfig,
  getConfig,
  updateConfig,
  getAnalyticsOverview,
  getAnalyticsQuestions,
  getAnalyticsThemes,
  summarizeAnalyticsThemes,
};

export default AssistantService;
